import "@/App.css";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { ADMIN_PORTAL } from "@/config";
import { analyticsAllowed } from "@/components/CookieConsent";
import PageLoader from "@/components/PageLoader";
import CookieConsent from "@/components/CookieConsent";
import ChatWidget from "@/components/ChatWidget";
import { ScrollProgress, BackToTop } from "@/components/ScrollTools";
import { CursorSpotlight, PageFade, ThemeBackdrop } from "@/components/fx";
import Landing from "@/pages/Landing";
import { Loader2, MailX } from "lucide-react";

const Creators = lazy(() => import("@/pages/Creators"));
const CreatorProfile = lazy(() => import("@/pages/CreatorProfile"));
const Apply = lazy(() => import("@/pages/Apply"));
const FanAccount = lazy(() => import("@/pages/FanAccount"));
const MagicLogin = lazy(() => import("@/pages/MagicLogin"));
const ApplySuccess = lazy(() => import("@/pages/ApplySuccess"));
const ApplicationStatus = lazy(() => import("@/pages/ApplicationStatus"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const AuthPages = lazy(() => import("@/pages/Auth"));
const Legal = lazy(() => import("@/pages/Legal"));
const Features = lazy(() => import("@/pages/Features"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Partners = lazy(() => import("@/pages/Partners"));
const Team = lazy(() => import("@/pages/Team"));
const Careers = lazy(() => import("@/pages/Careers"));
const TipPage = lazy(() => import("@/pages/TipPage"));
const ConfirmDeletion = lazy(() => import("@/pages/ConfirmDeletion"));
const Help = lazy(() => import("@/pages/Help"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Faq = lazy(() => import("@/pages/Faq"));
const Brands = lazy(() => import("@/pages/Brands"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const CreatorLogin = lazy(() => import("@/pages/creator/CreatorLogin"));
const CreatorDashboard = lazy(() => import("@/pages/creator/CreatorDashboard"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));

function FullLoader() {
  return (
    <div className="bg-dt-bg min-h-screen flex items-center justify-center" data-testid="route-loader">
      <Loader2 className="w-8 h-8 animate-spin text-dt-accent" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function SessionBeacon() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (!analyticsAllowed()) return;
    let sid = localStorage.getItem("dt-pv-session");
    if (!sid) {
      sid = `pv-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("dt-pv-session", sid);
    }
    api.post("/analytics/pv", { path: location.pathname, session_id: sid }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function Login() { return <AuthPages view="login" />; }
function Register() { return <AuthPages view="register" />; }
function VerifyEmail() { return <AuthPages view="verify" />; }
function ForgotPassword() { return <AuthPages view="forgot" />; }
function ResetPassword() { return <AuthPages view="reset" />; }
function LegalPrivacy() { return <Legal view="privacy" />; }
function LegalTerms() { return <Legal view="terms" />; }
function LegalCookies() { return <Legal view="cookies" />; }
function LegalAppPolicy() { return <Legal view="application-policy" />; }
function LegalDeletion() { return <Legal view="data-deletion" />; }
function ContactPage() { return <Legal view="contact" />; }
function StatusPage() { return <Legal view="status" />; }

function Unsubscribe() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("working");
  useEffect(() => {
    api.get("/unsubscribe", { params: { token: sp.get("token") || "" } })
      .then(() => setState("done"))
      .catch(() => setState("error"));
  }, []);
  return (
    <div className="bg-dt-bg min-h-screen text-dt-text flex items-center justify-center px-4" data-testid="unsubscribe-page">
      <div className="card-dt card-glow-once p-10 max-w-md text-center">
        <MailX className="w-10 h-10 text-dt-accent mx-auto mb-4" />
        {state === "working" && <p className="text-dt-muted flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Unsubscribing…</p>}
        {state === "done" && (
          <>
            <h1 className="font-heading text-2xl font-bold text-metallic">You're unsubscribed</h1>
            <p className="text-sm text-dt-muted mt-3">You were removed from that creator's mailing list immediately. No further emails will be sent.</p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-heading text-2xl font-bold text-metallic">Link not valid</h1>
            <p className="text-sm text-dt-muted mt-3">This unsubscribe link is invalid or was already used.</p>
          </>
        )}
        <Link to="/" className="btn-press inline-block mt-6 bg-dt-silver text-dt-bg rounded-full px-6 py-2.5 text-sm font-bold" data-testid="unsubscribe-home-btn">Back to Destiny Tip</Link>
      </div>
    </div>
  );
}

function SiteMeta() {
  // Favicon + browser tab title, manageable from the admin dashboard.
  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const title = r.data.site_title || r.data.platform_name;
      if (title) document.title = title;
      if (r.data.favicon_url) {
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = `${process.env.REACT_APP_BACKEND_URL}${r.data.favicon_url}`;
      }
    }).catch(() => {});
  }, []);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  // Key by the top-level section only: nested routers (admin, creator dashboard)
  // stay mounted and run their own inner transitions — no full remount/flicker.
  const sectionKey = "/" + (location.pathname.split("/")[1] || "");
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={sectionKey}>
        <Route path="/" element={<Landing />} />
        <Route path="/features" element={<PageFade><Features /></PageFade>} />
        <Route path="/pricing" element={<PageFade><Pricing /></PageFade>} />
        <Route path="/partners" element={<PageFade><Partners /></PageFade>} />
        <Route path="/team" element={<PageFade><Team /></PageFade>} />
        <Route path="/careers" element={<PageFade><Careers /></PageFade>} />
        <Route path="/brands" element={<PageFade><Brands /></PageFade>} />
        <Route path="/blog" element={<PageFade><Blog /></PageFade>} />
        <Route path="/blog/:slug" element={<PageFade><BlogPost /></PageFade>} />
        <Route path="/faq" element={<PageFade><Faq /></PageFade>} />
        <Route path="/creators" element={<PageFade><Creators /></PageFade>} />
        <Route path="/creators/:slug" element={<PageFade><CreatorProfile /></PageFade>} />
        <Route path="/creator/:slug" element={<PageFade><CreatorProfile /></PageFade>} />
        <Route path="/partner/:slug" element={<PageFade><CreatorProfile /></PageFade>} />
        <Route path="/login" element={<PageFade><Login /></PageFade>} />
        <Route path="/register" element={<PageFade><Register /></PageFade>} />
        <Route path="/verify-email" element={<PageFade><VerifyEmail /></PageFade>} />
        <Route path="/forgot-password" element={<PageFade><ForgotPassword /></PageFade>} />
        <Route path="/reset-password" element={<PageFade><ResetPassword /></PageFade>} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/confirm-deletion/:token" element={<PageFade><ConfirmDeletion /></PageFade>} />
        <Route path="/help" element={<PageFade><Help /></PageFade>} />
        <Route path="/help/:slug" element={<PageFade><Help /></PageFade>} />
        <Route path="/creator/login" element={<PageFade><CreatorLogin /></PageFade>} />
        <Route path="/creator/dashboard/*" element={<CreatorDashboard />} />
        <Route path="/apply" element={<Protected><PageFade><Apply /></PageFade></Protected>} />
        <Route path="/account" element={<Protected><PageFade><FanAccount /></PageFade></Protected>} />
        <Route path="/magic-login" element={<PageFade><MagicLogin /></PageFade>} />
        <Route path="/apply/success" element={<Protected><PageFade><ApplySuccess /></PageFade></Protected>} />
        <Route path="/application-status" element={<Protected><PageFade><ApplicationStatus /></PageFade></Protected>} />
        <Route path="/onboarding" element={<Protected><PageFade><Onboarding /></PageFade></Protected>} />
        <Route path="/privacy" element={<PageFade><LegalPrivacy /></PageFade>} />
        <Route path="/terms" element={<PageFade><LegalTerms /></PageFade>} />
        <Route path="/cookies" element={<PageFade><LegalCookies /></PageFade>} />
        <Route path="/application-policy" element={<PageFade><LegalAppPolicy /></PageFade>} />
        <Route path="/data-deletion" element={<PageFade><LegalDeletion /></PageFade>} />
        <Route path="/contact" element={<PageFade><ContactPage /></PageFade>} />
        <Route path="/status" element={<PageFade><StatusPage /></PageFade>} />
        <Route path="/admin/*" element={<PageFade><NotFound /></PageFade>} />
        <Route path={`${ADMIN_PORTAL}/login`} element={<PageFade><AdminLogin /></PageFade>} />
        <Route path={`${ADMIN_PORTAL}/*`} element={<AdminApp />} />
        <Route path="*" element={<PageFade><NotFound /></PageFade>} />
        <Route path="/:username" element={<PageFade><TipPage /></PageFade>} />
      </Routes>
    </AnimatePresence>
  );
}
function MaintenanceScreen() {
  return (
    <div className="bg-dt-bg min-h-screen text-dt-text flex items-center justify-center px-4" data-testid="maintenance-page">
      <div className="text-center max-w-md">
        <img src="/assets/logo.png" alt="" className="w-16 h-16 rounded-2xl object-cover ring-1 ring-dt-border mx-auto mb-6 loader-logo" />
        <p className="text-[11px] tracking-[0.3em] uppercase font-bold text-dt-accent mb-3">Scheduled maintenance</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-metallic">We'll be right back</h1>
        <p className="text-sm text-dt-muted mt-4 leading-relaxed">
          Destiny Tip is undergoing scheduled maintenance. Creator dashboards and the team console
          remain available — public pages return shortly.
        </p>
        <Link to={`${ADMIN_PORTAL}/login`} className="inline-block mt-8 text-xs text-dt-muted hover:text-dt-text transition-colors" data-testid="maintenance-staff-link">
          Team member? Sign in →
        </Link>
      </div>
    </div>
  );
}

function MaintenanceGate({ children }) {
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const path = location.pathname;
    const isStaffArea = path.startsWith(ADMIN_PORTAL) || path.startsWith("/admin") || path.startsWith("/creator")
      || ["/login", "/register", "/forgot-password", "/reset-password"].includes(path);
    if (isStaffArea) { setBlocked(false); return; }
    api.get("/settings/public")
      .then((r) => setBlocked(!!r.data.maintenance_mode))
      .catch((e) => setBlocked(e?.response?.status === 503));
  }, [location.pathname]);
  if (blocked) return <MaintenanceScreen />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageLoader />
            <SiteMeta />
            <ThemeBackdrop />
            <ScrollProgress />
            <CursorSpotlight />
            <SessionBeacon />
            <Suspense fallback={<FullLoader />}>
              <MaintenanceGate>
                <div className="relative z-[1]">
                  <AnimatedRoutes />
                </div>
              </MaintenanceGate>
            </Suspense>
            <BackToTop />
            <CookieConsent />
            <ChatWidget />
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#121923", border: "1px solid #27313E", color: "#F5F7FA" } }} />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
